/* ============================================================================
 * ez-engine-hitch.js — EZ-Snap Hitch Enclosure Kit engine (theme asset, vanilla JS)
 * Source of truth: demo/app/lib/hitch.js.
 *
 * PORTED LOGIC: the clamp-rounding rule (recClampPack — smallest prepack >= raw), the
 * BOM assembly (hitchBOM), and the multi-line cart payload (buildHitchLines). SKU scheme
 * ported; PRICE / VARIANT / SPEC data is INJECTED — Liquid builds the config from product
 * objects at render (the hitch kit + the shared skirting add-on ladders):
 *
 *   const hitch = window.EZSnap.createHitchEngine({
 *     price: 499.99, snapCount: 100, inclVelFt: 10, recClampRaw: 18,
 *     bagSku: "EZ-C-SKB-X", bagPrice: 99, bagLabel: "Hitch 74″", panelSize: "71″",
 *     colors: [{ code, name }, ...],
 *     clampPacks: [25,50,75,100],
 *     clampPrice: { 25,50,75,100 },
 *     velPrice:   { 10, 20 },
 *     variantId:  { kit(c), clamp(n), vel(ft), bag() }   // OPTIONAL resolvers
 *   });
 *
 * SNAP PACK (2026-10-07): the kit no longer carries snaps in the warehouse BOM. buildHitchLines always emits
 * its snap pack as a DISPLAY line (kitSnaps:true, price 0, no variantId): EZ-C-SNA-{snapCount}-{cap}-{stud},
 * cap = the panel color, stud = studColor (defaults to the panel color). The controller turns it into a real
 * cart line via window.EZKitSnaps.lines(); the server prices it.
 *
 * PUBLIC API:
 *   hitch.hitchSku(color)          -> "EZ-B-HIT-WC-BK"
 *   hitch.hitchTitle(color)        -> full presewn-panel title
 *   hitch.recClampPack([raw])      -> smallest clamp prepack >= raw (18 → 25)
 *   hitch.hitchBOM(color)          -> plain-language "what's included" list
 *   hitch.buildHitchLines(opts)    -> [{ sku, title, opts, qty, price, variantId? }, ...]
 * ============================================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api; // node/gate
  if (root && typeof root === "object") {
    root.EZSnap = root.EZSnap || {};
    root.EZSnap.createHitchEngine = api.createHitchEngine;
  }
})(typeof window !== "undefined" ? window : this, function () {
  "use strict";

  function createHitchEngine(cfg) {
    cfg = cfg || {};
    var HITCH = {
      price: cfg.price,
      panelSize: cfg.panelSize,
      snapCount: cfg.snapCount,
      inclVelFt: cfg.inclVelFt,
      recClampRaw: cfg.recClampRaw,
      bagSku: cfg.bagSku,
      bagPrice: cfg.bagPrice,
      bagLabel: cfg.bagLabel,
    };
    var COLORS = cfg.colors || [];
    var CLAMP_PACKS = cfg.clampPacks || [25, 50, 75, 100];
    var CLAMP_PRICE = cfg.clampPrice || {};
    var VEL_PRICE = cfg.velPrice || {};
    var VID = cfg.variantId || {};

    function colorName(c) {
      var hit = COLORS.find(function (x) { return x.code === c; });
      return (hit && hit.name) || "";
    }

    // conn: "NC" = No-connector variant (EZ-B-HIT-NC-{color}); anything else = WC (EZ-B-HIT-WC-{color}).
    var hitchSku = function (c, conn) { return "EZ-B-HIT-" + (conn === "NC" ? "NC-" : "WC-") + c; };
    var hitchTitle = function (c) {
      return "EZ-Snap 5th Wheel Front Hitch Enclosure Kit — " +
        HITCH.panelSize + " (zipper door), " + colorName(c);
    };

    // clamp rounding — smallest prepack whose count >= raw (skirting §5.3); 18 → 25
    function recClampPack(raw) {
      raw = raw == null ? HITCH.recClampRaw : raw;
      return CLAMP_PACKS.find(function (p) { return p >= raw; }) || 100;
    }

    // conn: "NC" drops the 10ft connector fabric (the No-connector variant — for rigs that already run
    // EZ-Snap side skirting, which supplies the connecting fabric). WC keeps it.
    function hitchBOM(c, conn) {
      var bom = ["Presewn " + HITCH.panelSize + " " + colorName(c) + " hitch enclosure panel with zipper door"];
      if (conn !== "NC") bom.push("10 ft of " + colorName(c) + " connector fabric (the strip that seals the back of the enclosure)");
      bom.push(HITCH.snapCount + " " + colorName(c) + " snaps");
      bom.push("Gecko Velcro™ 10 ft strip");
      bom.push("Hitch enclosure instructions");
      return bom;
    }

    function buildHitchLines(o) {
      o = o || {};
      var color = o.color;
      var connector = o.connector === "NC" ? "NC" : "WC";
      var studColor = o.studColor || color;   // studs default to the panel (material) color
      var clampsOn = o.clampsOn == null ? true : !!o.clampsOn;
      var clampPack = o.clampPack == null ? 25 : o.clampPack;
      var bagOn = !!o.bagOn;
      var velcroOn = !!o.velcroOn;
      var velFt = o.velFt || 10;

      var lines = [{
        sku: hitchSku(color, connector),
        title: "Hitch Enclosure Kit",
        opts: HITCH.panelSize + " zipper door · " + colorName(color) + (connector === "NC" ? " · No connector" : ""),
        qty: 1,
        price: o.kitPrice != null ? o.kitPrice : HITCH.price,
        variantId: VID.kit ? VID.kit(color, connector) : undefined,
      }];

      // The kit's snap pack (always — it left the kit BOM): one snapCount pack. Display line only (price 0, no
      // variantId); the controller resolves the real EZ-C-SNA variant via window.EZKitSnaps at add-to-cart.
      if (HITCH.snapCount > 0) {
        lines.push({
          sku: "EZ-C-SNA-" + HITCH.snapCount + "-" + color + "-" + studColor, title: "Snap Pack",
          opts: HITCH.snapCount + "-count · " + colorName(color) + " caps · " + colorName(studColor) + " studs",
          qty: 1, price: 0, kitSnaps: true, packN: HITCH.snapCount, cap: color, stud: studColor,
        });
      }
      if (clampsOn) {
        lines.push({
          sku: "EZ-B-CMP-" + clampPack, title: "Pipe Clamp Pack", opts: clampPack + "-count",
          qty: 1, price: CLAMP_PRICE[clampPack], variantId: VID.clamp ? VID.clamp(clampPack) : undefined,
        });
      }
      if (bagOn) {
        lines.push({
          sku: HITCH.bagSku, title: "Storage Bag", opts: HITCH.bagLabel,
          qty: 1, price: HITCH.bagPrice, variantId: VID.bag ? VID.bag() : undefined,
        });
      }
      if (velcroOn) {
        lines.push({
          sku: "EZ-C-VEL-" + velFt, title: "Gecko Velcro™", opts: velFt + " ft",
          qty: 1, price: VEL_PRICE[velFt], variantId: VID.vel ? VID.vel(velFt) : undefined,
        });
      }
      return lines;
    }

    return {
      hitchSku: hitchSku,
      hitchTitle: hitchTitle,
      recClampPack: recClampPack,
      hitchBOM: hitchBOM,
      buildHitchLines: buildHitchLines,
      colorName: colorName,
    };
  }

  return { createHitchEngine: createHitchEngine };
});
